<script lang="ts" setup>
import type { IncludedDeviation, Shipment, TourEvent } from '@/types/delivery-management'
import type { EventGroup } from '@/types/delivery-management-dispatcher-overview-widget'

import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { hideAllPoppers } from 'floating-vue'

import { TransactionProduct } from '@/types/transaction'
import { differenceFromMinutes, localeTime } from '@/utils/dates'
import { useMittListener } from '@/hooks/use-mitt-listener'

import PackagingTable, {
    type PackagingProduct,
} from '@/components/dashboard/widgets/delivery-management/DispatcherOverview/PackagingTable.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import DeviationPopoverCard from '@/components/dashboard/widgets/delivery-management/DispatcherOverview/DeviationPopoverCard.vue'
import ShipmentStatus from '@/components/delivery-management/ShipmentStatus.vue'
import ShipmentTypeBadge from '@/components/dashboard/widgets/delivery-management/DispatcherOverview/ShipmentTypeBadge.vue'

interface ProductSummary {
    id: string
    name: string
    image?: string
    transactionDelivered: number
    transactionReceived: number
    shipmentDelivered: number
    shipmentReceived: number
}

const props = defineProps<{
    eventGroup: EventGroup
    tourName: string
}>()

const { t } = useI18n()

const stopTime = computed(() => {
    const start = props.eventGroup.reportEvent?.startedAt
    const end = props.eventGroup.reportEvent?.endedAt

    if (!start || !end) {
        return null
    }

    return differenceFromMinutes(dayjs(end).diff(dayjs(start), 'minute'))
})

const packagingTableSummary = computed(() => {
    const productMap: Record<string, ProductSummary> = {}

    // Iterate through all events in this eventGroup
    props.eventGroup.events.forEach((event) => {
        // Add transaction products
        event.transactions?.forEach((transaction) => {
            transaction.products?.forEach((product: TransactionProduct) => {
                const existing = productMap[product.id] || {
                    id: product.id,
                    name: product.name || '—',
                    image: product.image,
                    transactionDelivered: 0,
                    transactionReceived: 0,
                    shipmentDelivered: 0,
                    shipmentReceived: 0,
                }

                existing.transactionDelivered += product.delivered ?? 0
                existing.transactionReceived += product.received ?? 0
                productMap[product.id] = existing
            })
        })

        // Add shipment products
        event.shipment?.products?.forEach((shipmentProduct: TransactionProduct) => {
            // For shipment products, productId contains the actual product ID (id is the pivot ID)
            const productId = shipmentProduct.productId ?? shipmentProduct.id
            const existing = productMap[productId] || {
                id: productId,
                name: shipmentProduct.name || '—',
                image: shipmentProduct.image,
                transactionDelivered: 0,
                transactionReceived: 0,
                shipmentDelivered: 0,
                shipmentReceived: 0,
            }

            existing.shipmentDelivered += shipmentProduct.delivered ?? 0
            existing.shipmentReceived += shipmentProduct.received ?? 0
            productMap[productId] = existing
        })
    })

    return Object.values(productMap)
})

const packagingTableSummaryProducts = computed(() =>
    packagingTableSummary.value.map(
        (product): PackagingProduct => ({
            id: product.id,
            name: product.name,
            image: product.image,
            delivered: product.transactionDelivered,
            received: product.transactionReceived,
            shipmentDelivered: product.shipmentDelivered,
            shipmentReceived: product.shipmentReceived,
            hasIssues:
                product.shipmentDelivered > 0 &&
                product.transactionDelivered > 0 &&
                product.transactionDelivered !== product.shipmentDelivered,
        }),
    ),
)

function getShipmentTotals(event: TourEvent<Shipment>): {
    totalWeight: number
    totalVolume: number
    totalItems: number
} {
    const collis = event.shipment?.collis || []

    return {
        totalWeight: collis.reduce(
            (acc, colli) => acc + (colli?.weight || 0) * (colli?.amount || 0),
            0,
        ),
        totalVolume: collis.reduce(
            (acc, colli) => acc + (colli?.volume || 0) * (colli?.amount || 0),
            0,
        ),
        totalItems: collis.reduce((acc, colli) => acc + (colli?.amount || 0), 0),
    }
}

function hasProductIssues(event: TourEvent<Shipment>, product: TransactionProduct): boolean {
    const shipmentDelivered = getShipmentProductQuantity(event, product.id, 'delivered')
    const hasShipmentProducts = !!(event.shipment?.products && event.shipment?.products?.length > 0)
    return (
        hasProductDeviation(event, product) ||
        (hasShipmentProducts &&
            ((product.delivered ?? 0) && product.delivered > 0) !== shipmentDelivered)
    )
}

function hasProductDeviation(tourEvent: TourEvent<Shipment>, product: TransactionProduct): boolean {
    return (
        tourEvent.shipment?.deviations?.some((deviation: IncludedDeviation) =>
            deviation?.note?.toLowerCase().includes((product?.name || '').toLowerCase()),
        ) || false
    )
}

function getShipmentProductQuantity(
    tourEvent: TourEvent<Shipment>,
    productId: string,
    field: 'delivered' | 'received',
): number {
    const shipmentProduct = tourEvent.shipment?.products?.find(
        (shipmentProduct: TransactionProduct) =>
            (shipmentProduct.productId ?? shipmentProduct.id) === productId,
    )
    return shipmentProduct?.[field] ?? 0
}

function getEventPackagingProducts(event: TourEvent<Shipment>): PackagingProduct[] {
    if (
        !event.transactions?.length ||
        !event.transactions.some((transaction) => transaction.products?.length)
    ) {
        return []
    }

    return event.transactions.flatMap(
        (transaction) =>
            transaction.products?.map(
                (product): PackagingProduct => ({
                    id: product.id,
                    name: product.name || '—',
                    image: product.image,
                    delivered: product.delivered ?? 0,
                    received: product.received ?? 0,
                    shipmentDelivered: getShipmentProductQuantity(event, product.id, 'delivered'),
                    shipmentReceived: getShipmentProductQuantity(event, product.id, 'received'),
                    hasIssues: hasProductIssues(event, product),
                }),
            ) || [],
    )
}

useMittListener('openDeviationModal', hideAllPoppers)
</script>

<template>
    <VMenu
        class="h-full block cursor-pointer w-1/3 max-w-sm min-w-56"
        popper-class="rounded-xl overflow-hidden drop-shadow-md"
        inner-class="bg-white dark:bg-dark-400"
        :triggers="['click']"
        :hide-triggers="['click', 'escape']"
        :interactive="true"
        :flip="false"
        :auto-hide="true"
        placement="bottom-start"
        :delay="{ hide: 0, show: 0 }"
    >
        <!--Trigger = the event tile-->
        <div class="h-full" v-bind="$attrs" role="button" tabindex="0">
            <slot />
        </div>

        <template #popper>
            <div
                class="relative w-[75rem] max-w-[90vw] bg-white dark:bg-dark-400"
                role="dialog"
                tabindex="-1"
            >
                <MyButton
                    v-close-popper
                    scheme="light"
                    size="micro"
                    icon
                    class="absolute right-2 top-2"
                >
                    <mdi:close class="w-4 h-4" />
                </MyButton>

                <div class="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-8">
                    <div
                        v-for="event in eventGroup.events"
                        :key="event.id"
                        class="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-dark-500 p-3 shadow-sm"
                    >
                        <div class="flex gap-3 justify-between">
                            <div class="w-1/4">
                                <div class="flex items-start justify-between gap-3">
                                    <div
                                        class="flex items-center gap-2 text-sm font-semibold text-black dark:text-white"
                                    >
                                        <span>{{
                                            event.shipment?.name ??
                                            event.shipment?.shipmentNumber ??
                                            t('shipment')
                                        }}</span>

                                        <ShipmentTypeBadge
                                            v-if="event.shipment"
                                            :shipment-type="event.shipment.type"
                                        />
                                    </div>

                                    <ShipmentStatus
                                        v-if="event.shipment"
                                        class="text-md font-semibold text-primary-500 dark:text-primary-100"
                                        :shipment="event.shipment"
                                    />
                                </div>

                                <div
                                    class="mt-0.5 text-xs text-gray-700 dark:text-gray-300 truncate font-medium"
                                >
                                    {{ event.shipment?.receiverName || t('receiver') }}
                                </div>

                                <!-- Totals stacked vertically (computed inline) -->
                                <div
                                    class="mt-2 border-t border-gray-100 dark:border-gray-600 pt-2 text-xs text-gray-700 dark:text-gray-300 space-y-0.5"
                                >
                                    <div>
                                        {{ t('totalKg') }}:
                                        <span
                                            class="font-semibold"
                                            v-text="getShipmentTotals(event).totalWeight"
                                        />
                                        {{ t('kg') }}
                                    </div>
                                    <div>
                                        {{ t('totalVolume') }}:
                                        <span
                                            class="font-semibold"
                                            v-text="getShipmentTotals(event).totalVolume"
                                        />
                                        {{ t('cbm') }}
                                    </div>
                                    <div>
                                        {{ t('totalItems') }}:
                                        <span
                                            class="font-semibold"
                                            v-text="getShipmentTotals(event).totalItems"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div v-if="getEventPackagingProducts(event).length > 0" class="w-1/3">
                                <PackagingTable
                                    :products="getEventPackagingProducts(event)"
                                    variant="transaction"
                                />
                            </div>

                            <DeviationPopoverCard
                                v-if="event.shipment && event.shipment.deviations?.length > 0"
                                :deviations="event.shipment.deviations"
                            />
                        </div>
                    </div>

                    <div
                        v-if="!eventGroup.events?.length"
                        class="py-6 text-center text-gray-500 dark:text-gray-400 text-sm"
                    >
                        {{ t('noShipmentsAtThisStop') }}
                    </div>
                </div>

                <div
                    class="border-t border-black dark:border-gray-600 bg-gray-200 dark:bg-dark-500 px-4 py-2.5 pb-8 flex flex-wrap items-end justify-between gap-3 text-xs text-gray-700 dark:text-gray-300"
                >
                    <div>
                        <span class="mr-3">
                            {{ t('shipments') }}:
                            <span
                                class="font-semibold"
                                v-text="`${eventGroup.shipmentsDone}/${eventGroup.shipmentsTotal}`"
                            />
                        </span>
                        <span class="mr-3">
                            {{ t('packagingProducts') }}:
                            <span
                                class="font-semibold"
                                v-text="eventGroup.packagingProductsCount"
                            />
                        </span>
                        <template v-if="eventGroup.reportEvent">
                            <span
                                class="mr-3"
                                v-text="
                                    t('arrivalEntity', {
                                        time: localeTime(eventGroup.reportEvent.startedAt),
                                    })
                                "
                            />
                            <span
                                v-if="eventGroup.reportEvent.endedAt"
                                class="mr-3"
                                v-text="
                                    t('departureEntity', {
                                        time: localeTime(eventGroup.reportEvent.endedAt),
                                    })
                                "
                            />
                            <span
                                v-if="stopTime"
                                class="mr-3"
                                v-text="t('stopTimeEntity', { value: stopTime })"
                            />
                            <span
                                v-if="eventGroup.reportEvent.waitTime"
                                v-text="
                                    t('waitTimeEntity', {
                                        value: differenceFromMinutes(
                                            eventGroup.reportEvent.waitTime,
                                        ),
                                    })
                                "
                            />
                        </template>
                    </div>

                    <div v-if="packagingTableSummaryProducts.length > 0">
                        <div class="mt-4">
                            <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                {{ t('packagingTableSummary') }}
                            </h4>
                            <PackagingTable
                                :products="packagingTableSummaryProducts"
                                variant="summary"
                                :empty-message="t('noPackagingDataAvailable')"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </VMenu>
</template>
